<script setup>
import { computed, watch, onMounted, getCurrentInstance } from 'vue';
import viewModule from '../../store/modules/view';

// Components
import OdpCourseDatesCoursesMenu from '../../bespoke/odp/OdpCourseDatesCoursesMenu';
import OdpCourseDatesSchoolsMenu from '../../bespoke/odp/OdpCourseDatesSchoolsMenu';
import ListingOptionsSearchModalBtn from '../../common/listing/ListingOptionsSearchModalBtn';
import SearchFiltersResetBtn from '../../common/listing/SearchFiltersResetBtn';
import ListingOptionsPaginationMetrics from '../../common/listing/ListingOptionsPaginationMetrics';
import ListingOptionsPreviousBtn from '../../common/listing/ListingOptionsPreviousBtn';
import ListingOptionsNextBtn from '../../common/listing/ListingOptionsNextBtn';
import ListingOptionsMenu from '../../common/listing/ListingOptionsMenu';
import TableView from '../../common/listing/TableView';
import GridView from '../../common/listing/GridView';
import Button from "../../common/Button.vue";
import SearchFilterToolbar from "../../common/search-filters/SearchFilterToolbar.vue";

const props = defineProps({
    options: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['hide-modal']);

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State Destructuring ---
const { formId, listingName, onSelect, selectedIds, hiddenFilters } = props.options;

// --- Module Registration Logic ---

const viewId = computed(() => {
    if (formId === 'pageEditorForm') {
        return 'view:pageEditorSelectListModal';
    }
    return 'view:' + listingName;
});

// Watch for viewId changes and register the module immediately
// This handles the "race condition" where children load before the store is ready.
watch(viewId, (newId) => {
    if (newId && !store.hasModule(newId)) {
        store.registerModule(newId, viewModule);
    }
}, { immediate: true });

// --- Computed State ---

const view = computed(() => store.state[viewId.value] || {});

const tableName = computed(() => view.value.tableName || '');

const viewMode = computed(() => view.value.viewMode);

const addNew = computed(() => {
    if (!view.value.componentId) return false;
    const component = store.state.components.items.find(o => o.id === view.value.componentId);
    return component ? !!component.addNew : false;
});

const show = computed(() => {
    return (
        store.getters['user/access'](tableName.value, 'add')
        && !view.value.showArchive
        && tableName.value !== 'm_basket_orders'
        && addNew.value
    );
});

const isOdp = computed(() => {
    const sitename = store.state.sitename || '';
    const table = tableName.value || '';
    return (
        sitename.indexOf('outdoorsproject') === 0 &&
        table.indexOf('odp_course_dates') === 0
    );
});

// --- Methods ---

const showAddItemModal = () => {
    store.dispatch('modals/show', {
        componentName: 'AddItemModal',
        obj: {
            tableName: tableName.value,
            onAddItem: (obj) => {
                // Logic for adding item to selection can be placed here
            }
        }
    });
};

// --- Lifecycle ---

onMounted(() => {
    // We use nextTick to ensure the module registration has fully settled
    // in the store before we fire the init dispatch.
    proxy.$nextTick(() => {
        store.dispatch(viewId.value + '/init', {
            listingName,
            selectedIds,
            hiddenFilters,
            selectMode: true,
            onSelect
        });
    });
});
</script>

<template>
    <div class="modal-dialog modal-xl">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">Select an item</h4>
                <button type="button" class="btn-close" @click="$emit('hide-modal')" aria-label="Close"></button>
            </div>

            <div v-if="view.tableName" class="modal-body p-0">
                <header>
                    <div class="d-flex border-bottom">
                        <div class="flex-fill">
                            <Button v-if="show" class="rounded-0" @click.native="showAddItemModal">
                                <i class="bi-plus-lg"/>
                            </Button>

                            <ListingOptionsSearchModalBtn :view-id="viewId"/>
                            <SearchFiltersResetBtn :view-id="viewId"/>
                        </div>

                        <div class="d-flex align-items-center">
                            <ListingOptionsPaginationMetrics :view-id="viewId"/>
                            <ListingOptionsPreviousBtn class="ms-1" :view-id="viewId"/>
                            <ListingOptionsNextBtn class="ms-1" :view-id="viewId"/>
                            <ListingOptionsMenu class="ms-1" :view-id="viewId"/>
                        </div>
                    </div>

                    <SearchFilterToolbar :view-id="viewId"/>
                </header>

                <div v-if="isOdp">
                    <OdpCourseDatesCoursesMenu :view-id="viewId"/>
                    <OdpCourseDatesSchoolsMenu :view-id="viewId"/>
                </div>

                <TableView v-if="viewMode === 'table' || !viewMode" :view-id="viewId" class="m-3"/>
                <GridView v-else-if="viewMode === 'thumbnail'" :view-id="viewId" class="m-3"/>
            </div>

            <div v-else class="modal-body p-5 text-center">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading listing...</span>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* No additional styles required as per original */
</style>